import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { Plus, MapPin, Trash2, Navigation2, Eye, Pencil } from 'lucide-react';

/**
 * LocationsTab — full-page Add/Edit Location flow now lives at
 * `/cases/:caseId/locations/new` and `/cases/:caseId/locations/:id/edit`
 * (LocationFormPage). Dialog removed.
 */
const LocationsTab = ({
  caseId,
  locations,
  canEdit,
  isAdmin,
  token,
  onRefresh,
  onViewItem,
  onDeleteItem,
}) => {
  const navigate = useNavigate();

  const handleDeleteLocation = async (locationId) => {
    if (onDeleteItem) return onDeleteItem('location', locationId, 'Delete this location?');
    if (!window.confirm('Delete this location?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/locations/${locationId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      onRefresh();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const getDirections = (location) => {
    const fullAddress = [location.address, location.city, location.state, location.zip_code].filter(Boolean).join(', ');
    const params = new URLSearchParams();
    if (location.latitude && location.longitude) {
      params.set('destination_lat', String(location.latitude));
      params.set('destination_lng', String(location.longitude));
    }
    if (fullAddress) params.set('destination_address', fullAddress);
    if (location.name) params.set('destination_label', location.name);
    params.set('directions', '1');
    navigate(`/case-address-map?${params.toString()}`);
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            onClick={() => navigate(`/cases/${caseId}/locations/new`)} data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Location
          </Button>
        </div>
      )}
      {locations.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <MapPin className="w-12 h-12 mx-auto text-slate-400 mb-2" />
            <p className="text-slate-500">No locations tracked yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {locations.map((location) => (
            <Card key={location.id} className="bg-white border-slate-200 hover:border-[#0F172A]/30 transition-colors" data-testid={`location-card-${location.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <button onClick={() => onViewItem(location, 'location')} className="text-left flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-slate-900 font-semibold truncate">{location.name}</h3>
                      <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs">
                        {location.location_type?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </button>
                  {isAdmin && (
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteLocation(location.id); }}
                      className="text-slate-500 hover:text-red-500 shrink-0" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  {location.address && <p className="text-slate-700">{location.address}</p>}
                  {(location.city || location.state || location.zip_code) && (
                    <p className="text-slate-500">{[location.city, location.state, location.zip_code].filter(Boolean).join(', ')}</p>
                  )}
                  {(location.latitude && location.longitude) && (
                    <p className="text-xs text-slate-500 font-mono">
                      GPS: {Number(location.latitude).toFixed(6)}, {Number(location.longitude).toFixed(6)}
                    </p>
                  )}
                  {location.description && <p className="text-slate-500 text-xs mt-2">{location.description}</p>}
                  {location.notes && <p className="text-slate-500 text-xs italic">{location.notes}</p>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={() => onViewItem(location, 'location')} data-testid={`location-view-${location.id}`} title="View details">
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {canEdit && (
                    <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      onClick={() => navigate(`/cases/${caseId}/locations/${location.id}/edit`)}
                      data-testid={`location-edit-${location.id}`} title="Edit location">
                      <Pencil className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-[#1D4ED8]/30 text-[#1D4ED8] hover:bg-[#1D4ED8] hover:text-white"
                    onClick={() => getDirections(location)} data-testid={`get-directions-${location.id}`}
                    disabled={!location.latitude && !location.address} title="Open in Address Intel Map with route">
                    <Navigation2 className="w-4 h-4 mr-1" /> Directions
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationsTab;
